import React, { useState } from "react";
import styled from "styled-components";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";

const PageContainer = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const RolesPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const CandidatesPanel = styled.div`
  flex: 1;
  min-width: 0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  padding: 16px;
`;

const RoleHeader = styled.div`
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
`;

const RoleLink = styled.a`
  font-size: 1.1rem;
  font-weight: 600;
  color: #227b7e;
  text-decoration: none;

  &:hover {
    color: #0f4d4f;
    text-decoration: underline;
  }
`;

const RoleDetails = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  margin-top: 4px;
`;

const RoleCard = styled.div<{ selected?: boolean }>`
  background-color: ${(props) => (props.selected ? "rgba(34, 123, 126, 0.08)" : "white")};
  border: 1px solid ${(props) => (props.selected ? "#227b7e" : "rgba(148, 163, 184, 0.35)")};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 150ms ease;

  &:hover {
    background-color: ${(props) => (props.selected ? "rgba(34, 123, 126, 0.12)" : "rgba(34, 123, 126, 0.06)")};
  }
`;

const RoleCardTitle = styled.div`
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
`;

const RoleCardDetails = styled.div`
  font-size: 0.85rem;
  color: #64748b;
`;

const CandidateCard = styled.div`
  background-color: white;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  transition: all 150ms ease;

  &:hover {
    border-color: rgba(34, 123, 126, 0.3);
  }
`;

const CandidateName = styled.div`
  width: 40%;
`;

const CandidateLink = styled.a`
  font-weight: 500;
  color: #227b7e;
  text-decoration: none;

  &:hover {
    color: #0f4d4f;
    text-decoration: underline;
  }
`;

type MatchStrength = "very-strong" | "strong" | "medium" | "weak" | "very-weak";

const getMatchStrength = (score: number): { label: string; strength: MatchStrength } => {
  if (score >= 90) return { label: "Very Strong", strength: "very-strong" };
  if (score >= 75) return { label: "Strong", strength: "strong" };
  if (score >= 60) return { label: "Medium", strength: "medium" };
  if (score >= 40) return { label: "Weak", strength: "weak" };
  return { label: "Very Weak", strength: "very-weak" };
};

const strengthColors: Record<MatchStrength, string> = {
  "very-strong": "#198754",
  "strong": "#20c997",
  "medium": "#fd7e14",
  "weak": "#dc3545",
  "very-weak": "#6c757d",
};

const ScoreColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 30%;
`;

const ScoreLabel = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  margin-bottom: 2px;
  text-align: left;
`;

const MatchScore = styled.div<{ strength: MatchStrength }>`
  font-weight: bold;
  font-size: 0.85rem;
  color: ${(props) => strengthColors[props.strength]};
`;

const StyledSelectTrigger = styled(SelectTrigger)`
  width: auto;
  min-width: 110px;
  font-size: 0.85rem;
  padding: 4px 28px 4px 8px;
  height: auto;
`;

const EmptyState = styled.div`
  color: #64748b;
  text-align: center;
  padding: 40px 20px;
  font-size: 0.95rem;
`;

interface Role {
  id: number;
  clientName: string;
  title: string;
  location: string;
  workType: string;
}

interface SuggestedCandidate {
  id: number;
  firstName: string;
  lastInitial: string;
  matchScore: number;
}

const placeholderRoles: Role[] = [
  { id: 1, clientName: "Acme Corp", title: "Senior Software Engineer", location: "Sydney, NSW", workType: "Permanent" },
  { id: 2, clientName: "TechStart", title: "Product Manager", location: "Melbourne, VIC", workType: "Contract" },
  { id: 3, clientName: "Global Finance", title: "Data Analyst", location: "Brisbane, QLD", workType: "Permanent" },
  { id: 4, clientName: "HealthTech", title: "UX Designer", location: "Perth, WA", workType: "Contract" },
  { id: 5, clientName: "RetailPlus", title: "DevOps Engineer", location: "Adelaide, SA", workType: "Permanent" },
];

const placeholderCandidates: Record<number, SuggestedCandidate[]> = {
  1: [
    { id: 101, firstName: "Sarah", lastInitial: "J", matchScore: 92 },
    { id: 102, firstName: "Michael", lastInitial: "C", matchScore: 85 },
    { id: 103, firstName: "Emma", lastInitial: "W", matchScore: 78 },
  ],
  2: [
    { id: 201, firstName: "James", lastInitial: "T", matchScore: 88 },
    { id: 202, firstName: "Lisa", lastInitial: "M", matchScore: 72 },
  ],
  3: [
    { id: 301, firstName: "David", lastInitial: "K", matchScore: 95 },
    { id: 302, firstName: "Rachel", lastInitial: "B", matchScore: 81 },
    { id: 303, firstName: "Tom", lastInitial: "S", matchScore: 67 },
    { id: 304, firstName: "Anna", lastInitial: "L", matchScore: 55 },
  ],
  4: [
    { id: 401, firstName: "Chris", lastInitial: "P", matchScore: 90 },
  ],
  5: [
    { id: 501, firstName: "Alex", lastInitial: "R", matchScore: 83 },
    { id: 502, firstName: "Jordan", lastInitial: "H", matchScore: 76 },
  ],
};

const userScoreOptions: { value: MatchStrength | ""; label: string }[] = [
  { value: "", label: "Select..." },
  { value: "very-strong", label: "Very Strong" },
  { value: "strong", label: "Strong" },
  { value: "medium", label: "Medium" },
  { value: "weak", label: "Weak" },
  { value: "very-weak", label: "Very Weak" },
];

const Experiments = (): JSX.Element => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [userScores, setUserScores] = useState<Record<number, MatchStrength | "">>({});

  const selectedRole = selectedRoleId
    ? placeholderRoles.find((r) => r.id === selectedRoleId)
    : null;

  const selectedCandidates = selectedRoleId
    ? placeholderCandidates[selectedRoleId] || []
    : [];

  const handleUserScoreChange = (candidateId: number, value: MatchStrength | "") => {
    setUserScores((prev) => ({ ...prev, [candidateId]: value }));
  };

  return (
    <PageContainer>
      <PageHeader title="Experiments" subtitle="Discover your history" />
      <ContentWrapper>
        <RolesPanel>
          {placeholderRoles.map((role) => (
            <RoleCard
              key={role.id}
              selected={selectedRoleId === role.id}
              onClick={() => setSelectedRoleId(role.id)}
            >
              <RoleCardTitle>{role.title}</RoleCardTitle>
              <RoleCardDetails>
                {role.clientName} &bull; {role.location} &bull; {role.workType}
              </RoleCardDetails>
            </RoleCard>
          ))}
        </RolesPanel>

        <CandidatesPanel>
          {!selectedRoleId ? (
            <EmptyState>Select a role to see suggested candidates</EmptyState>
          ) : (
            <>
              <RoleHeader>
                <RoleLink href={`/my-roles/${selectedRole?.id}`}>
                  {selectedRole?.title}
                </RoleLink>
                <RoleDetails>
                  {selectedRole?.clientName} &bull; {selectedRole?.location} &bull; {selectedRole?.workType}
                </RoleDetails>
              </RoleHeader>
              {selectedCandidates.length === 0 ? (
                <EmptyState>No candidates found for this role</EmptyState>
              ) : (
                selectedCandidates.map((candidate) => (
                  <CandidateCard key={candidate.id}>
                    <CandidateName>
                      <CandidateLink href={`/my-candidates/${candidate.id}`}>
                        {candidate.firstName} {candidate.lastInitial}.
                      </CandidateLink>
                    </CandidateName>
                    <ScoreColumn>
                      <ScoreLabel>System</ScoreLabel>
                      <MatchScore strength={getMatchStrength(candidate.matchScore).strength}>
                        {getMatchStrength(candidate.matchScore).label}
                      </MatchScore>
                    </ScoreColumn>
                    <ScoreColumn>
                      <ScoreLabel>Your Score</ScoreLabel>
                      <Select
                        value={userScores[candidate.id] || ""}
                        onValueChange={(value) =>
                          handleUserScoreChange(candidate.id, value as MatchStrength | "")
                        }
                      >
                        <StyledSelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </StyledSelectTrigger>
                        <SelectContent>
                          {userScoreOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </ScoreColumn>
                  </CandidateCard>
                ))
              )}
            </>
          )}
        </CandidatesPanel>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Experiments;
